import filter from 'lodash/filter'
import get from 'lodash/get'
import groupBy from 'lodash/groupBy'
import isEmpty from 'lodash/isEmpty'
import reduce from 'lodash/reduce'
import uniqBy from 'lodash/uniqBy'
import values from 'lodash/values'

import { formatNameWithCode } from 'utils/formatters'

import { EMPTY_CELL_CONTENT } from 'components/Core/Table/Cell'

import { relayConnectionToArray } from './graphql'

export const STYLE_COLOR_CANCELLED_INFO = 'styleColorsInOrder'
export const STYLE_COLOR_REPLACEMENT_INFO = 'replacementStyleColorInOrder'

export const POSITIVE_SIGN = 'positiveSign'
export const NEGATIVE_SIGN = 'negativeSign'

export const getStyleColorInfoInOrder = (order, styleColorId, salesType) => {
  const styleColorsInfoInOrder = get(order, salesType, [])
  const chosenStyleColorId = styleColorsInfoInOrder.find(
    (styleColorInfoInOrder) =>
      styleColorInfoInOrder.styleColorId === styleColorId,
  )
  if (!chosenStyleColorId) {
    return {
      totalUnits: 0,
      stylePrice: {
        wholesalePrice: '0',
      },
      onOrder: false,
    }
  }
  if (isEmpty(chosenStyleColorId.stylePrice)) {
    return {
      ...chosenStyleColorId,
      stylePrice: {
        wholesalePrice: '0',
      },
    }
  }
  return chosenStyleColorId
}

export const getSalesDifference = (
  order,
  cancellationStyleId,
  replacementStyleId,
) => {
  const cancellationStylesInfo = get(order, STYLE_COLOR_CANCELLED_INFO, [])
  const replacementStylesInfo = get(order, STYLE_COLOR_REPLACEMENT_INFO, [])

  const cancellationStyleColorInfo = cancellationStylesInfo.find(
    (styleColorInfoInOrder) =>
      styleColorInfoInOrder.styleColorId === cancellationStyleId,
  )

  const replacementStyleColorInfo = replacementStylesInfo.find(
    (styleColorInfoInOrder) =>
      styleColorInfoInOrder.styleColorId === replacementStyleId,
  )

  if (!cancellationStyleColorInfo || !replacementStyleColorInfo) {
    return 0
  }

  if (!cancellationStyleColorInfo.onOrder) {
    return 0
  }

  const affectedUnits = get(cancellationStyleColorInfo, 'totalUnits', 0)
  const cancellationPrice = get(
    cancellationStyleColorInfo,
    'stylePrice.wholesalePrice',
    0,
  )
  const replacementPrice = get(
    replacementStyleColorInfo,
    'stylePrice.wholesalePrice',
    0,
  )

  return affectedUnits * (replacementPrice - cancellationPrice)
}

export const getSalesReplacedPerCurrency = (
  orders,
  cancellationStyleColorId,
  replacementStyleColorId,
) =>
  orders.reduce((acc, order) => {
    const currencyCode = get(
      order.priceType,
      'currency.code',
      EMPTY_CELL_CONTENT,
    )
    const {
      stylePrice: { wholesalePrice },
    } = getStyleColorInfoInOrder(
      order,
      replacementStyleColorId,
      STYLE_COLOR_REPLACEMENT_INFO,
    )
    const { totalUnits: cancellationTotalUnits, onOrder } =
      getStyleColorInfoInOrder(
        order,
        cancellationStyleColorId,
        STYLE_COLOR_CANCELLED_INFO,
      )

    const orderPrice = parseInt(
      (onOrder ? cancellationTotalUnits : 0) * wholesalePrice,
      10,
    )
    acc[currencyCode] = (acc[currencyCode] || 0) + orderPrice
    return acc
  }, {})

export const getOrdersCurrencyCodes = (orders) =>
  orders.reduce((currencies, order) => {
    const code = get(order, 'priceType.currency.code', EMPTY_CELL_CONTENT)
    if (currencies.indexOf(code) === -1) {
      currencies.push(code)
    }
    return currencies
  }, [])

export const getSalesDifferencesPerCurrency = (
  orders,
  cancellationStyleColorId,
  replacementStyleColorId,
) =>
  orders.reduce((salesDifferenceTotal, order) => {
    const currencyCode = get(
      order.priceType,
      'currency.code',
      EMPTY_CELL_CONTENT,
    )
    const salesDifference = getSalesDifference(
      order,
      cancellationStyleColorId,
      replacementStyleColorId,
    )
    return {
      ...salesDifferenceTotal,
      [currencyCode]:
        (salesDifferenceTotal[currencyCode] || 0) + salesDifference,
    }
  }, {})

export const getTotalUnitsForStyleColor = (orders, styleColorId, salesType) =>
  reduce(
    orders,
    (totalAcc, order) => {
      const styleColorsInOrder = get(order, salesType, [])
      const styleColorInOrderForGivenStyleColorId = styleColorsInOrder.find(
        (styleColorInOrder) => styleColorInOrder.styleColorId === styleColorId,
      )
      if (
        !styleColorInOrderForGivenStyleColorId ||
        !styleColorInOrderForGivenStyleColorId.onOrder
      ) {
        return totalAcc
      }
      return styleColorInOrderForGivenStyleColorId.totalUnits + totalAcc
    },
    0,
  )

export const getOrdersAffectedByStyleColor = (
  orders,
  styleColorId,
  salesType,
) =>
  reduce(
    orders,
    (totalAcc, order) => {
      const styleColorsInOrder = get(order, salesType, [])
      const styleColorInOrderForGivenStyleColorId = styleColorsInOrder.find(
        (styleColorInOrder) => styleColorInOrder.styleColorId === styleColorId,
      )
      if (!styleColorInOrderForGivenStyleColorId) {
        return totalAcc
      }
      return styleColorInOrderForGivenStyleColorId.onOrder
        ? totalAcc + 1
        : totalAcc
    },
    0,
  )

/**
 * Given a list of items returns unique values only
 */
export const getUniqueValues = (list) =>
  list.filter((value, index, self) => self.indexOf(value) === index)

export const getDivisionNames = (order) =>
  relayConnectionToArray(order.divisions).map((divisions) => divisions.name)

export const getDivisionCodes = (order) =>
  relayConnectionToArray(order.divisions).map((divisions) => divisions.code)

export const getCollectionNames = (order) =>
  relayConnectionToArray(order.collections).map(
    (collection) => collection && collection.name,
  )

export const getExportStatus = (order) =>
  order.latestExports &&
  order.latestExports.length &&
  (order.latestExports.every(
    (latestExport) => order.latestExports[0].status === latestExport.status,
  )
    ? order.latestExports[0].status
    : 'MULTIPLE')

export const addTitleToEvents = (orderEvents) =>
  orderEvents
    ? orderEvents.map((orderEvent) => ({
        ...orderEvent,
        title: formatNameWithCode(orderEvent.name, orderEvent.code),
      }))
    : []

export const getLineItems = (lineItems) =>
  lineItems
    ? {
        totalPrice: lineItems.totalPrice,
        discountedPrice: lineItems.discountedPrice,
        totalUnits: lineItems.totalUnits,
        items: relayConnectionToArray(lineItems),
      }
    : {}

export const getBudgetsTotal = (budgets) =>
  budgets
    ? relayConnectionToArray(budgets).reduce(
        (total, budget) => total + budget.wholesaleTotal,
        0,
      )
    : 0

export const getTotalLineItemsUnits = (orders) =>
  orders
    ? orders.reduce(
        (res, order) =>
          res +
          (order.lineItems ? parseInt(order.lineItems.totalUnits, 10) : 0),
        0,
      )
    : 0

export const getOrdersFilteredByStyleColor = (orders, styleColorId) =>
  Array.isArray(orders)
    ? orders.filter((order) => {
        const styleColorsInOrder = get(order, 'styleColorsInOrder', [])
        const styleColorInOrderForGivenStyleColorId = styleColorsInOrder.find(
          (styleColorInOrder) =>
            styleColorInOrder.styleColorId === styleColorId,
        )
        if (!styleColorInOrderForGivenStyleColorId) {
          return false
        }
        return styleColorInOrderForGivenStyleColorId.onOrder
      })
    : []

export const getStyles = (lineItems) =>
  relayConnectionToArray(lineItems).reduce(
    (res, lineItem) => (lineItem.style ? res.concat(lineItem.style) : res),
    [],
  )

export const getUniqueStyleNames = (orders) =>
  orders
    ? getUniqueValues(
        orders.reduce(
          (res, order) =>
            order && order.styles
              ? res.concat(order.styles.map((style) => style.name))
              : res,
          [],
        ),
      )
    : []

export const filterByNonEmptyLineItems = (orders) =>
  filter(orders, (order) => get(order, 'lineItems.items', []).length > 0)

export const getLineItemsByUniqueStyleColors = (orders) =>
  orders.map((order) => ({
    ...order,
    lineItems: {
      ...order.lineItems,
      items: uniqBy(get(order, 'lineItems.items', []), 'styleColor.id'),
    },
  }))

export const groupLineItemsByStyle = (orders) =>
  orders.reduce((ordersAcc, order) => {
    if (!get(order, 'lineItems.items')) {
      return ordersAcc
    }
    const {
      lineItems: { items },
      ...orderWithoutLineItem
    } = order
    const styleColorsByStyle = values(groupBy(items, 'styleColor.style.id'))
    return ordersAcc.concat({
      ...orderWithoutLineItem,
      uniqueStyles: styleColorsByStyle.reduce((uniqueStyleAcc, styleColors) => {
        const style = get(styleColors, '[0].styleColor.style')
        if (style) {
          const { images, ...styleWithoutImages } = style
          return uniqueStyleAcc.concat({
            ...styleWithoutImages,
            image: get(relayConnectionToArray(images), '[0].image.url', ''),
            styleColors: styleColors.map(({ styleColor }) => {
              const { style: _styleFromStyleColor, ...styleColorWithoutStyle } =
                styleColor
              return styleColorWithoutStyle
            }),
          })
        }
        return uniqueStyleAcc
      }, []),
    })
  }, [])

export const filterOrdersBySelectedStyleColors = (
  orders,
  selectedStyleColors,
) => {
  const filteredOrders = orders.filter(
    ({ id }) => id in selectedStyleColors && !isEmpty(selectedStyleColors[id]),
  )

  return filteredOrders.map((filteredOrder) => {
    if (!get(filteredOrder, 'uniqueStyles')) {
      return filteredOrder
    }

    const filteredUniqueStyles = filteredOrder.uniqueStyles
      .map((style) => {
        const filteredStyleColors = style.styleColors.filter(
          ({ id }) => id in selectedStyleColors[filteredOrder.id],
        )
        return { ...style, styleColors: filteredStyleColors }
      })
      .filter(({ styleColors }) => !isEmpty(styleColors))

    return { ...filteredOrder, uniqueStyles: filteredUniqueStyles }
  })
}

export const getSalesCancelledPerCurrency = (
  orders,
  cancellationStyleColorId,
) =>
  orders.reduce((acc, order) => {
    const code = get(order.priceType, 'currency.code', EMPTY_CELL_CONTENT)
    const {
      totalUnits,
      stylePrice: { wholesalePrice },
      onOrder,
    } = getStyleColorInfoInOrder(
      order,
      cancellationStyleColorId,
      STYLE_COLOR_CANCELLED_INFO,
    )
    const orderSale = onOrder ? totalUnits * wholesalePrice : 0
    const price = parseInt(orderSale, 10)
    acc[code] = (acc[code] || 0) + price
    return acc
  }, {})

export const getSignColor = (difference) => {
  let signColor
  if (difference > 0) {
    signColor = POSITIVE_SIGN
  }
  if (difference < 0) {
    signColor = NEGATIVE_SIGN
  }
  return signColor
}

export const isModifiedSinceLastExport = (latestExports, orderModifiedDate) =>
  latestExports.some((orderExportDate) => {
    if (orderExportDate.status === 'SCHEDULED') return false
    return new Date(orderModifiedDate) > new Date(orderExportDate.created)
  })
